import {
  AuthorizationRequest,
  DefaultCrypto,
  LocalStorageBackend,
  BasicQueryStringUtils,
  FetchRequestor,
  AuthorizationNotifier,
  AuthorizationServiceConfiguration,
  GRANT_TYPE_AUTHORIZATION_CODE,
  TokenRequest,
  RedirectRequestHandler,
  BaseTokenRequestHandler,
  GRANT_TYPE_REFRESH_TOKEN
} from "@openid/appauth";
import axios from "axios";

class NoHashQueryStringUtils extends BasicQueryStringUtils {
  parse(input, useHash) {
      return super.parse(input, false /* never use hash */);
  }
}

/**
 * LocationLike handed to appauth's RedirectRequestHandler. appauth redirects
 * to the auth server with `assign()`, which PUSHES a history entry; the page
 * that triggered login then sits behind the post-login page, and Back does a
 * full reload of it instead of going to the previous page. Replacing the
 * entry keeps history as [previous page, post-login page] (ENG-26629).
 * Reads proxy to window.location so appauth still parses the callback URL.
 */
export const replacingLocation = {
  get hash() { return window.location.hash; },
  get host() { return window.location.host; },
  get origin() { return window.location.origin; },
  get hostname() { return window.location.hostname; },
  get pathname() { return window.location.pathname; },
  get port() { return window.location.port; },
  get protocol() { return window.location.protocol; },
  get search() { return window.location.search; },
  assign(url) {
    window.location.replace(url);
  },
};

class Auth {
  constructor() {
    this.scope = "offline openid";
    this.isLoggedIn = false;
    this.userDetails = undefined;


    this.notifier = new AuthorizationNotifier();
    this.authorizationHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      replacingLocation,
      new DefaultCrypto()
    );
    this.tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());
    // set notifier to deliver responses
    this.authorizationHandler.setAuthorizationNotifier(this.notifier);
    // set a listener to listen for authorization responses
    this.notifier.setAuthorizationListener(async (request, response, error) => {
      // console.log('Authorization listener request complete ', request, response, error);
      if (error) {
        this.isLoggedIn = false;
        this.error = error;
      } else if (response) {
        this.request = request;
        this.response = response;
        this.code = response.code;
        this.error = null;
        this.isLoggedIn = true;
      }
    });
    this.authorizationHandler.completeAuthorizationRequestIfPossible();
  }

  getAccessToken() {
    if (this.tokenResponse) {
      return this.tokenResponse.accessToken;
    } else {
      throw new Error("No access token found! First authorize.");
    }
  }

  async authorize() {
    // Already authenticated in this SPA session (e.g. /callback remounted via
    // back navigation): return the cached user instead of re-running the token
    // exchange with an already-consumed authorization code (ENG-26629).
    if (this.isLoggedIn && this.userDetails) {
      return this.userDetails;
    }
    if (!this.configuration) {
      await this.fetchServiceConfiguration();
    }
    await this.authorizationHandler.completeAuthorizationRequestIfPossible();
    if (!this.isLoggedIn) {
      this.makeAuthorizationRequest();
    } else {
      await this.getRefreshToken();
      await this.fetchUserDetails();
      return this.userDetails;
    }
  }

  getUserDetails() {
    return this.userDetails;
  }

  logOut() {
    delete this.code;
    this.isLoggedIn = false;
    // Clear TARS-namespaced session data to prevent stale cache across logins
    sessionStorage.removeItem("KWS_FACTORY_CACHE");
    sessionStorage.removeItem("TARS_ACCESS_TOKEN");
    sessionStorage.removeItem("TARS_IS_INTERNAL");
    sessionStorage.removeItem("TARS_MODULES_ACCESS");
    sessionStorage.removeItem("BOUNCER_PERMISSIONS");
    sessionStorage.removeItem("BOUNCER_PERMISSIONS_KEY_SET_HASH");
    return this.configuration
      ? this.configuration.endSessionEndpoint
      : undefined;
  }

  async getRefreshToken() {
    let extras = undefined;
    if (this.request && this.request.internal) {
      extras = {};
      extras["code_verifier"] = this.request.internal["code_verifier"];
    }
    // use the code to make the token request.
    let request = new TokenRequest({
      client_id: window.config.REACT_APP_CLIENTID,
      redirect_uri: window.config.REACT_APP_REDIRECTURI,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
      code: this.code,
      refresh_token: undefined,
      extras: extras,
    });

    if (!this.configuration) {
      return Promise.reject("Service configuration not set");
    } else {
      return this.tokenHandler
        .performTokenRequest(this.configuration, request)
        .then((response) => {
          this.tokenResponse = response;
        })
        .catch((err) => {
          // Swallowed on purpose: callers treat a missing tokenResponse as
          // "not authenticated" and re-trigger login. Log so a failed code
          // exchange is visible in the console instead of a silent blank page.
          console.error("Token exchange failed:", err);
        });
    }
  }

  async fetchUserDetails() {
    if (
      this.configuration &&
      this.configuration.userInfoEndpoint &&
      this.tokenResponse &&
      this.tokenResponse.accessToken
    ) {
      const headers = new Headers([
        ["Authorization", "Bearer " + this.tokenResponse.accessToken],
      ]);
      const request = new Request(this.configuration.userInfoEndpoint);
      return fetch(request, { headers })
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Userinfo request failed with status ${response.status}`
            );
          }
          return response.json();
        })
        .then((userdata) => {
          this.userDetails = {
            id: userdata.sub.split(';').pop(),
            name: userdata.name,
            email: userdata.email,
            phone: userdata.phone_number,
            image_url: userdata.picture,
            roles: userdata.roles,
          };
        });
    } else {
      this.userDetails = null;
    }
  }

  showMessage(message) {
    alert(message);
  }

  async fetchServiceConfiguration() {
    let configuration;
    try {
      configuration = await AuthorizationServiceConfiguration.fetchFromIssuer(
        window.config.REACT_APP_OPENIDCONNECTURL, new FetchRequestor()
      );
    } catch (error) {
      // console.log('Something bad happened', error);
      this.showMessage(`Could not fetch auth service configuration: ${error}`);
      throw error;
    }
    this.configuration = configuration;
    return configuration;
  }

  makeAuthorizationRequest() {
    // create a request
    const authRequest = new AuthorizationRequest({
      client_id: window.config.REACT_APP_CLIENTID,
      redirect_uri: window.config.REACT_APP_REDIRECTURI,
      scope: 'openid offline',
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      state: JSON.stringify(Object.assign({}, { pathname: window.location.pathname, host: window.location.host, search: window.location.search })),
      extras: { prompt: "consent", access_type: "offline" },
    });

    if (this.configuration) {
      this.authorizationHandler.performAuthorizationRequest(
        this.configuration,
        authRequest
      );
    } else {
      this.showMessage(
        "Fetch Authorization Service configuration, before you make the authorization request."
      );
    }
  }

  async getRefreshedAccessToken() {
    let configuration;
    if (!this.configuration) {
      configuration = await this.fetchServiceConfiguration();
    } else {
      configuration = this.configuration;
    }
    if (this.tokenResponse) {
      let request = new TokenRequest({
        client_id: this.clientId,
        redirect_uri: this.redirectUri,
        grant_type: GRANT_TYPE_REFRESH_TOKEN,
        code: undefined,
        refresh_token: this.tokenResponse.refreshToken,
        extras: undefined,
      });
      let response = await this.tokenHandler.performTokenRequest(
        configuration,
        request
      );
      this.tokenResponse.accessToken = response.accessToken;
      this.tokenResponse.issuedAt = response.issuedAt;
      this.tokenResponse.expiresIn = response.expiresIn;
      this.tokenResponse.tokenType = response.tokenType;
      this.tokenResponse.scope = response.scope;
      return this.tokenResponse.accessToken;
    } else {
      await this.authorize();
      return this.tokenResponse ? this.tokenResponse.accessToken : undefined;
    }
  }

  getTarsAccessToken() {
    if (
      this.userDetails &&
      this.tokenResponse &&
      this.tokenResponse.accessToken
    ) {
      try {
        var data = JSON.stringify({"user_id": this.userDetails.email});
        var config = {
          method: 'get',
          url: window.config.REACT_APP_APIGATEWAY + '/tva/v1/user/auth/issue-access-token?access_token=' + this.tokenResponse.accessToken,
          headers: { 
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.tokenResponse.accessToken}`,
          },
          data : data
        };
        return axios(config);
      } catch (err) {
        console.log(err)
      }
    }
  }

  static get instance() {
    // Try to get an efficient singleton
    if (!this['singleton']) {
      this['singleton'] = new Auth();
    }
    return this['singleton'];
  }
}

export default Auth.instance;